// ============================================================
// TaskFlow OS - Workflow (Nodes & Edges) Store
// ============================================================

import { create } from 'zustand';
import type { OperationalNode, ApprovalNode, DesignNode, WorkflowEdge, WorkflowType } from '../lib/types';
import { supabase, logEvent } from '../lib/supabase';

interface WorkflowDataState {
  operationalNodes: OperationalNode[];
  approvalNodes: ApprovalNode[];
  designNodes: DesignNode[];
  edges: WorkflowEdge[];
  loading: boolean;

  // Operational Nodes
  fetchOperationalNodes: (workflowId: string) => Promise<void>;
  createOperationalNode: (node: Partial<OperationalNode>) => Promise<{ data: OperationalNode | null; error: string | null }>;
  updateOperationalNode: (id: string, updates: Partial<OperationalNode>) => Promise<{ error: string | null }>;
  deleteOperationalNode: (id: string) => Promise<{ error: string | null }>;

  // Approval Nodes
  fetchApprovalNodes: (workflowId: string) => Promise<void>;
  createApprovalNode: (node: Partial<ApprovalNode>) => Promise<{ data: ApprovalNode | null; error: string | null }>;
  updateApprovalNode: (id: string, updates: Partial<ApprovalNode>) => Promise<{ error: string | null }>;
  deleteApprovalNode: (id: string) => Promise<{ error: string | null }>;

  // Design Nodes
  fetchDesignNodes: (workflowId: string) => Promise<void>;
  createDesignNode: (node: Partial<DesignNode>) => Promise<{ data: DesignNode | null; error: string | null }>;
  updateDesignNode: (id: string, updates: Partial<DesignNode>) => Promise<{ error: string | null }>;
  deleteDesignNode: (id: string) => Promise<{ error: string | null }>;

  // Edges
  fetchEdges: (workflowId: string) => Promise<void>;
  createEdge: (edge: Partial<WorkflowEdge>) => Promise<{ data: WorkflowEdge | null; error: string | null }>;
  deleteEdge: (id: string) => Promise<{ error: string | null }>;

  // Bulk
  fetchAllForWorkflow: (workflowId: string) => Promise<void>;
  clearAll: () => void;
}

export const useWorkflowDataStore = create<WorkflowDataState>((set, get) => ({
  operationalNodes: [],
  approvalNodes: [],
  designNodes: [],
  edges: [],
  loading: false,

  // ---- Operational Nodes ----

  fetchOperationalNodes: async (workflowId) => {
    const { data } = await supabase
      .from('operational_nodes')
      .select('*, assignee:profiles!assignee_id(id, display_name, avatar_url)')
      .eq('workflow_id', workflowId)
      .order('sort_order');

    if (data) set({ operationalNodes: data as OperationalNode[] });
  },

  createOperationalNode: async (node) => {
    const { data, error } = await supabase
      .from('operational_nodes')
      .insert(node)
      .select('*, assignee:profiles!assignee_id(id, display_name, avatar_url)')
      .single();

    if (error) return { data: null, error: error.message };

    const n = data as OperationalNode;
    set({ operationalNodes: [...get().operationalNodes, n] });

    await logEvent('node_created', { title: n.title, type: 'operational' }, {
      workflowId: n.workflow_id,
      nodeId: n.id,
      nodeType: 'operational',
    });
    return { data: n, error: null };
  },

  updateOperationalNode: async (id, updates) => {
    const { error } = await supabase
      .from('operational_nodes')
      .update(updates)
      .eq('id', id);

    if (error) return { error: error.message };

    set({
      operationalNodes: get().operationalNodes.map((n) =>
        n.id === id ? { ...n, ...updates } : n
      ),
    });
    return { error: null };
  },

  deleteOperationalNode: async (id) => {
    const node = get().operationalNodes.find((n) => n.id === id);
    const { error } = await supabase.from('operational_nodes').delete().eq('id', id);

    if (error) return { error: error.message };

    set({
      operationalNodes: get().operationalNodes.filter((n) => n.id !== id),
      edges: get().edges.filter((e) => e.source_node_id !== id && e.target_node_id !== id),
    });

    if (node) {
      await logEvent('node_deleted', { title: node.title }, {
        workflowId: node.workflow_id,
        nodeId: id,
        nodeType: 'operational',
      });
    }
    return { error: null };
  },

  // ---- Approval Nodes ----

  fetchApprovalNodes: async (workflowId) => {
    const { data } = await supabase
      .from('approval_nodes')
      .select('*, approver:profiles!approver_id(id, display_name, avatar_url)')
      .eq('workflow_id', workflowId);

    if (data) set({ approvalNodes: data as ApprovalNode[] });
  },

  createApprovalNode: async (node) => {
    const { data, error } = await supabase
      .from('approval_nodes')
      .insert(node)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const n = data as ApprovalNode;
    set({ approvalNodes: [...get().approvalNodes, n] });
    return { data: n, error: null };
  },

  updateApprovalNode: async (id, updates) => {
    const { error } = await supabase.from('approval_nodes').update(updates).eq('id', id);
    if (error) return { error: error.message };

    set({
      approvalNodes: get().approvalNodes.map((n) =>
        n.id === id ? { ...n, ...updates } : n
      ),
    });
    return { error: null };
  },

  deleteApprovalNode: async (id) => {
    const { error } = await supabase.from('approval_nodes').delete().eq('id', id);
    if (error) return { error: error.message };

    set({
      approvalNodes: get().approvalNodes.filter((n) => n.id !== id),
      edges: get().edges.filter((e) => e.source_node_id !== id && e.target_node_id !== id),
    });
    return { error: null };
  },

  // ---- Design Nodes ----

  fetchDesignNodes: async (workflowId) => {
    const { data } = await supabase
      .from('design_nodes')
      .select('*, assignee:profiles!assignee_id(id, display_name, avatar_url)')
      .eq('workflow_id', workflowId);

    if (data) set({ designNodes: data as DesignNode[] });
  },

  createDesignNode: async (node) => {
    const { data, error } = await supabase
      .from('design_nodes')
      .insert(node)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const n = data as DesignNode;
    set({ designNodes: [...get().designNodes, n] });
    return { data: n, error: null };
  },

  updateDesignNode: async (id, updates) => {
    const { error } = await supabase.from('design_nodes').update(updates).eq('id', id);
    if (error) return { error: error.message };

    set({
      designNodes: get().designNodes.map((n) =>
        n.id === id ? { ...n, ...updates } : n
      ),
    });
    return { error: null };
  },

  deleteDesignNode: async (id) => {
    const { error } = await supabase.from('design_nodes').delete().eq('id', id);
    if (error) return { error: error.message };

    set({
      designNodes: get().designNodes.filter((n) => n.id !== id),
      edges: get().edges.filter((e) => e.source_node_id !== id && e.target_node_id !== id),
    });
    return { error: null };
  },

  // ---- Edges ----

  fetchEdges: async (workflowId) => {
    const { data } = await supabase
      .from('workflow_edges')
      .select('*')
      .eq('workflow_id', workflowId);

    if (data) set({ edges: data as WorkflowEdge[] });
  },

  createEdge: async (edge) => {
    const { data, error } = await supabase
      .from('workflow_edges')
      .insert(edge)
      .select()
      .single();

    if (error) return { data: null, error: error.message };

    const e = data as WorkflowEdge;
    set({ edges: [...get().edges, e] });
    return { data: e, error: null };
  },

  deleteEdge: async (id) => {
    const { error } = await supabase.from('workflow_edges').delete().eq('id', id);
    if (error) return { error: error.message };

    set({ edges: get().edges.filter((e) => e.id !== id) });
    return { error: null };
  },

  // ---- Bulk Operations ----

  fetchAllForWorkflow: async (workflowId) => {
    set({ loading: true });
    await Promise.all([
      get().fetchOperationalNodes(workflowId),
      get().fetchApprovalNodes(workflowId),
      get().fetchDesignNodes(workflowId),
      get().fetchEdges(workflowId),
    ]);
    set({ loading: false });
  },

  clearAll: () =>
    set({
      operationalNodes: [],
      approvalNodes: [],
      designNodes: [],
      edges: [],
    }),
}));
